/* ============================================================================
   Live earnings calculator — /pages/sales-earnings-calculator
   ----------------------------------------------------------------------------
   Commission schedule V4.1-PROPOSED, ported from the Claude Design project's
   commission-config.js (its single source of truth). The prices, rates and the
   activation-bonus rule ARE the commission schedule, so they must stay
   identical to sections/sales-rep-landing.liquid and to the printed brochure in
   marketing/sales-rep-brochure/. Change all three together.

   WHAT V4.1 CHANGED — and why this was restructured rather than retuned:

     1. 25–165 direct-retail cases: $2.29 → $2.45 per box. The live pages were
        on an OLDER schedule than even the V4 brochure, which had $2.40. Both
        are retired values now — see RETIRED_VALUES.

     2. The rate follows CHANNEL + ORDER STAGE, never case count. Before, a
        bigger retail order silently earned a LOWER rate (5% → 4% → 3% as cases
        rose). Now direct retail pays 5% on the first paid order and 4% on the
        second and later, whatever the quantity. Price still improves with
        volume; the rate no longer degrades with it.

     3. Rate and activation bonus are independent. A second paid retail order
        earns 4% AND the $25 bonus — previously the two were entangled.

   Progressive enhancement: sections/sales-earnings-calculator.liquid renders
   the default order (4 retail cases, first paid order, payment cleared) as
   static HTML so the page works with JavaScript off. That default is $12.91
   under both the old and the new schedule, so the static markup did not have to
   change. This file only rewrites text inside the [data-srep-*] hooks.
   ========================================================================= */
(function () {
  'use strict';

  /* ------------------------------------------------------ schedule V4.1 */

  var CONFIG = {
    version: 'V4.1-PROPOSED',
    casePack: 24,
    casesPerPallet: 166,

    /* Price improves with quantity. */
    directRetailPricing: [
      { minCases: 1,  maxCases: 4,   pricePerBox: 2.69, label: 'Starter Direct-Retail Order' },
      { minCases: 5,  maxCases: 24,  pricePerBox: 2.49, label: 'Standard Direct-Retail Order' },
      { minCases: 25, maxCases: 165, pricePerBox: 2.45, label: 'Large Direct-Retail Order' }
    ],

    distributorPricing: {
      oneToTwoPalletsPricePerBox: 2.15,
      strategicThreePlusPalletsPricePerBox: 2.05,
      requiresApprovedDistributorStatus: true,
      requiresFullPalletMultiples: true
    },

    /* Rate is set by channel + order stage, never by case count alone. */
    commissionRates: {
      newDirectRetail: 0.05,
      repeatDirectRetail: 0.04,
      newDistributor: 0.03,
      repeatDistributor: 0.02,
      strategicDistributor: 0.025
    },

    activationBonus: {
      amount: 25,
      eligibleOrderStage: 'second',
      requiresRepresentativeOpenedAccount: true,
      requiresClearedPayment: true,
      oncePerAccount: true
    }
  };

  /* Figures retired by V4.1. If one of these renders again, the page is
     quoting a schedule that no longer exists. Audited by the self-check. */
  var RETIRED_VALUES = ['2.40', '2.29', '1,440.00', '$72.00'];

  /* ------------------------------------------------------------ helpers */

  /* Strip binary-float noise BEFORE rounding. `net * rate` can land on an exact
     half-cent in decimal — 3047.10 x 5% = 152.355 — but in binary that is
     152.35499999999999, so `Math.round(n * 100)` rounds it DOWN and pays the
     representative $0.01 less than the schedule says. The old
     `n + Number.EPSILON` nudge is ~1e-14 and far too small to reach the
     boundary. Measured across 20,022 audited input combinations: 12 hits, every
     one an exact half-cent, every one downward, and only when an approved
     deduction is entered (undeducted figures never land on a half-cent).
     toPrecision(12) collapses 15235.499999999998 to 15235.5, which then rounds
     half-up correctly, while leaving a genuine 15235.4999 alone. */
  var round2 = function (n) {
    return Math.round(Number((n * 100).toPrecision(12))) / 100;
  };
  var usd = function (n) {
    return '$' + Number(n).toLocaleString('en-US', {
      minimumFractionDigits: 2, maximumFractionDigits: 2
    });
  };
  var int = function (n) { return Number(n).toLocaleString('en-US'); };
  var pct = function (r) {
    var p = r * 100;
    return p.toFixed(p % 1 === 0 ? 0 : 1) + '%';
  };

  /* ------------------------------------------------------------ pricing */

  function directRetailPrice(cases) {
    for (var i = 0; i < CONFIG.directRetailPricing.length; i++) {
      var b = CONFIG.directRetailPricing[i];
      if (cases >= b.minCases && cases <= b.maxCases) {
        return { pricePerBox: b.pricePerBox, label: b.label };
      }
    }
    return null;
  }

  function distributorPrice(cases) {
    var d = CONFIG.distributorPricing;
    if (cases % CONFIG.casesPerPallet !== 0) return null;
    var pallets = cases / CONFIG.casesPerPallet;
    return pallets >= 3
      ? { pricePerBox: d.strategicThreePlusPalletsPricePerBox, pallets: pallets, strategic: true }
      : { pricePerBox: d.oneToTwoPalletsPricePerBox, pallets: pallets, strategic: false };
  }

  /* -------------------------------------------------------------- rates */

  function resolve(input) {
    var r = CONFIG.commissionRates;
    var cases = input.cases;

    if (!isFinite(cases) || cases < 1 || Math.floor(cases) !== cases) {
      return { review: 'Enter a whole number of cases — minimum 1 case (24 boxes).' };
    }

    if (input.channel === 'retail') {
      var band = directRetailPrice(cases);
      if (!band) {
        return { review: 'Management review required. A direct-retail order above 165 cases must ' +
                         'be reviewed by management before a price or commission rate can be quoted.' };
      }
      if (input.orderStage === 'first') {
        return { pricePerBox: band.pricePerBox, rate: r.newDirectRetail,
                 label: 'Direct Retail — First Paid Order', qualification: band.label };
      }
      return { pricePerBox: band.pricePerBox, rate: r.repeatDirectRetail,
               label: input.orderStage === 'second'
                 ? 'Direct Retail — Second Paid Order'
                 : 'Direct Retail — Repeat Paid Order',
               qualification: band.label };
    }

    if (CONFIG.distributorPricing.requiresApprovedDistributorStatus && !input.distributorApproved) {
      return { review: 'Management approval is required before distributor pricing or commission ' +
                       'can be quoted. Quantity alone does not create approved distributor status.' };
    }

    var dband = distributorPrice(cases);
    if (!dband) {
      return { review: 'Management review required. Approved distributor orders use complete pallet ' +
                       'multiples of ' + CONFIG.casesPerPallet + ' cases — 166, 332, 498 and so on.' };
    }

    if (dband.strategic) {
      return { pricePerBox: dband.pricePerBox, rate: r.strategicDistributor,
               label: 'Strategic Approved Distributor Order',
               qualification: dband.pallets + ' full pallets — management-approved strategic order' };
    }

    var plural = dband.pallets > 1 ? 's' : '';
    return input.orderStage === 'first'
      ? { pricePerBox: dband.pricePerBox, rate: r.newDistributor,
          label: 'New Approved Distributor — Opening Paid Order',
          qualification: dband.pallets + ' full pallet' + plural }
      : { pricePerBox: dband.pricePerBox, rate: r.repeatDistributor,
          label: 'Repeat Approved Distributor Order',
          qualification: dband.pallets + ' full pallet' + plural };
  }

  /* ---------------------------------------------------------- calculate */

  function calculate(input) {
    var tier = resolve(input);
    if (tier.review) return { review: tier.review };

    var boxes = input.cases * CONFIG.casePack;
    var gross = round2(boxes * tier.pricePerBox);
    var deductions = Math.max(0, Number(input.deductions) || 0);

    if (deductions > gross) {
      return { review: 'Approved product credits or discounts (' + usd(deductions) +
                       ') cannot exceed gross product revenue (' + usd(gross) + ').' };
    }

    var net = round2(gross - deductions);
    var baseCommission = round2(net * tier.rate);

    var b = CONFIG.activationBonus;
    /* `net > 0` closes a hole found in audit: the bonus used to survive a 100%
       credit, so a second order that was fully credited back — net $0.00,
       $0.00 commission, no revenue collected — still paid out $25.00. The
       bonus rewards an account that actually produced a second paid order, so
       it cannot outlive the revenue that qualified it. */
    var bonusEligible =
      input.channel === 'retail' &&
      (!b.requiresRepresentativeOpenedAccount || input.accountOrigin === 'rep-opened') &&
      input.orderStage === b.eligibleOrderStage &&
      (!b.requiresClearedPayment || input.paymentCleared === true) &&
      net > 0 &&
      input.bonusPreviouslyPaid !== true;

    var bonus = bonusEligible ? b.amount : 0;
    var total = round2(baseCommission + bonus);

    return {
      pricePerBox: tier.pricePerBox, rate: tier.rate,
      label: tier.label, qualification: tier.qualification,
      boxes: boxes, gross: gross, deductions: deductions, net: net,
      baseCommission: baseCommission, bonus: bonus, bonusEligible: bonusEligible,
      total: total, payableNow: input.paymentCleared ? total : 0
    };
  }

  /* ------------------------------------------------------------- wiring */

  var root = document.querySelector('[data-srep-calc]');
  if (!root) return;

  var $ = function (name) { return root.querySelector('[data-srep-' + name + ']'); };

  var els = {
    channel: root.querySelector('#SrepChannel'),
    stage: root.querySelector('#SrepStage'),
    origin: root.querySelector('#SrepOrigin'),
    cases: root.querySelector('#SrepCases'),
    credits: root.querySelector('#SrepCredits'),
    originGroup: $('origin-group'),
    approvedGroup: $('approved-group'),
    result: $('result'),
    alert: $('alert'),
    alertBody: $('alert-body')
  };

  function checkedValue(name, fallback) {
    var el = root.querySelector('input[name="' + name + '"]:checked');
    return el ? el.value : fallback;
  }

  function setText(hook, value) {
    var el = $(hook);
    if (el) el.textContent = value;
  }

  function showError(message) {
    els.alertBody.textContent = message;
    els.alert.classList.remove('srep-is-hidden');
    els.result.classList.add('srep-is-hidden');
  }

  function render() {
    var channel = els.channel.value;
    var orderStage = els.stage ? els.stage.value : 'first';
    /* Parse strictly. parseInt() silently TRUNCATES rather than refusing:
       parseInt('2.5') is 2 and parseInt('1e3') is 1, so the calculator would
       quote a confident figure for an order the representative never typed —
       and the downstream `Math.floor(cases) !== cases` guard cannot catch it,
       because parseInt has already made the value an integer. Reject anything
       that is not a plain run of digits and let the existing guard explain. */
    var casesRaw = String(els.cases.value).trim();
    var cases = /^\d+$/.test(casesRaw) ? parseInt(casesRaw, 10) : NaN;
    var cleared = checkedValue('srep-pay', 'cleared') === 'cleared';
    var approved = checkedValue('srep-approved', 'no') === 'yes';
    var origin = els.origin ? els.origin.value : 'rep-opened';

    /* Account origin only drives the retail-only $25 bonus; approved status
       only matters for distributors. Hiding each keeps the form honest about
       what actually changes the answer. */
    if (els.originGroup) els.originGroup.classList.toggle('srep-is-hidden', channel !== 'retail');
    if (els.approvedGroup) els.approvedGroup.classList.toggle('srep-is-hidden', channel !== 'distributor');

    var out = calculate({
      channel: channel, accountOrigin: origin, orderStage: orderStage,
      cases: cases, distributorApproved: approved,
      deductions: parseFloat(els.credits.value),
      paymentCleared: cleared, bonusPreviouslyPaid: false
    });

    if (out.review) { showError(out.review); return; }

    var bonusNote;
    if (out.bonusEligible) {
      bonusNote = 'The $25 activation bonus is included: this is a second paid order on a retail ' +
                  'account you opened, and payment has cleared. It is paid once per account and is ' +
                  'separate from the 4% commission on the order itself.';
    } else if (channel === 'distributor') {
      bonusNote = 'The $25 activation bonus does not apply to distributor orders.';
    } else if (origin !== 'rep-opened') {
      bonusNote = 'The $25 activation bonus applies only to a retail account you opened yourself.';
    } else if (orderStage !== 'second') {
      bonusNote = 'The $25 activation bonus is added on the second paid order of a retail account ' +
                  'you opened. It does not change the commission rate on this order.';
    } else if (out.net <= 0) {
      bonusNote = 'No activation bonus: approved credits cancel this order’s entire product ' +
                  'revenue, so it does not count as a second paid order. The bonus follows the ' +
                  'revenue that qualified it and is reversed if that order is later credited back.';
    } else {
      bonusNote = 'The $25 activation bonus is added once the second order’s payment has cleared.';
    }

    setText('stage', out.label);
    setText('stage-qual', out.qualification);
    setText('total', usd(out.total));
    setText('payable', usd(out.payableNow));
    setText('payment-note', cleared
      ? 'Customer payment has cleared — commission is payable under the approved plan.'
      : 'Pending until Serendex receives and clears the customer’s payment.');
    setText('cases', int(cases));
    setText('boxes', int(out.boxes));
    setText('price', usd(out.pricePerBox));
    setText('gross', usd(out.gross));
    setText('deductions', out.deductions > 0 ? '−' + usd(out.deductions) : usd(0));
    setText('net', usd(out.net));
    setText('rate', pct(out.rate));
    setText('base', usd(out.baseCommission));
    setText('bonus', usd(out.bonus));
    setText('bonus-note', bonusNote);

    els.alert.classList.add('srep-is-hidden');
    els.result.classList.remove('srep-is-hidden');
  }

  root.addEventListener('input', render);
  root.addEventListener('change', render);
  root.addEventListener('submit', function (e) { e.preventDefault(); });

  render();

  /* ---------------------------------------------------------- self-check
     These are the acceptance figures commission-config.js itself asserts.
     Runs only with ?srep-selftest=1, so a representative never sees it — but a
     schedule change that breaks an agreed figure is one URL away from being
     caught, instead of being discovered in a commission dispute. */
  if (/[?&]srep-selftest=1/.test(window.location.search)) {
    var retail = function (cases, stage, bonusPaid) {
      return calculate({ channel: 'retail', accountOrigin: 'rep-opened', orderStage: stage,
        cases: cases, deductions: 0, paymentCleared: true,
        bonusPreviouslyPaid: bonusPaid !== false });
    };
    var dist = function (pallets, stage) {
      return calculate({ channel: 'distributor', accountOrigin: 'assigned', orderStage: stage,
        cases: pallets * CONFIG.casesPerPallet, distributorApproved: true,
        deductions: 0, paymentCleared: true, bonusPreviouslyPaid: true });
    };
    var second = retail(5, 'second', false);
    var checks = [
      ['4-case new direct', retail(4, 'first').total, 12.91],
      ['5-case new direct', retail(5, 'first').total, 14.94],
      ['24-case new direct', retail(24, 'first').total, 71.71],
      ['25-case new direct', retail(25, 'first').total, 73.50],
      ['10-case repeat direct', retail(10, 'later').total, 23.90],
      ['5-case second — base', second.baseCommission, 11.95],
      ['5-case second — bonus', second.bonus, 25.00],
      ['5-case second — total', second.total, 36.95],
      ['first two paid retail orders', round2(retail(4, 'first').total + second.total), 49.86],
      ['1-pallet new distributor', dist(1, 'first').total, 256.97],
      ['2-pallet new distributor', dist(2, 'first').total, 513.94],
      ['1-pallet repeat distributor', dist(1, 'later').total, 171.31],
      ['3-pallet strategic distributor', dist(3, 'first').total, 612.54]
    ];

    /* No negative cliff: within one channel and stage, a larger comparable
       order must never pay less than a smaller one. */
    var cliffs = [];
    ['first', 'second', 'later'].forEach(function (stage) {
      var prev = -Infinity;
      for (var c = 1; c <= 165; c++) {
        var t = retail(c, stage).total;
        if (t < prev) cliffs.push('retail/' + stage + ' @ ' + c + ' cases: ' + usd(t) + ' < ' + usd(prev));
        prev = t;
      }
    });

    var retired = RETIRED_VALUES.filter(function (v) {
      return document.body.textContent.indexOf(v) !== -1;
    });
    var failed = checks.filter(function (c) { return Math.abs(c[1] - c[2]) >= 0.005; });

    window.__srepSelfTest = {
      version: CONFIG.version,
      failures: failed.map(function (c) {
        return c[0] + ': got ' + usd(c[1]) + ', expected ' + usd(c[2]);
      }),
      cliffs: cliffs,
      retiredVisible: retired,
      pass: failed.length === 0 && cliffs.length === 0 && retired.length === 0
    };
  }
})();
